import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, MenuItem, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { API_URLS, ResDataType } from "src/api/apiUrls";
import useApiServices from "src/api/useApiServices";
import { Block } from "src/components/Block";
import AppButton from "src/components/common/AppButton";
import AppLoadingAndErrorWrapper from "src/components/common/AppLoadingAndErrorWrapper";
import AppLoadingButton from "src/components/common/AppLoadingButton";
import { ConfirmDialog } from "src/components/custom-dialog";
import FormProvider, { RHFSelect } from "src/components/hook-form";
import { Upload } from "src/components/upload";
import { useBoolean } from "src/hooks/use-boolean";
import { AddCategoryBadgeSchema, NewCtgBadgeSchema } from "src/schemas";
import { BadgeDetailsDtoType, IAddCategoryBadge } from "src/types/badges";
import { UploadFileResponseType } from "src/types/upload-file-res";

interface IAddCategoryBadgeProps {
  open: boolean;
  onClose: VoidFunction;
  id?: string | number;
}

const AddCategoryBadge: React.FC<IAddCategoryBadgeProps> = props => {
  // #region States
  const confirm = useBoolean();
  const { open, onClose, id } = props;
  const isEditing = !(id === null || id === undefined);
  const { usePostApi, useGetListApi, usePatchApi, useGetItemApi } =
    useApiServices();
  const [featureImage, setFeatureImage] = useState<File | string | null>(null);
  const [dataToSend, setDataToSend] = useState<IAddCategoryBadge>();
  // #endregion States

  // #region Form
  const methods = useForm<IAddCategoryBadge>({
    defaultValues: {
      featureImage: "",
      programCategoryId: undefined,
    },
    resolver: yupResolver(NewCtgBadgeSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;
  // #endregion Form

  // #region Services
  const { data: programCategories } = useGetListApi<
    ResDataType["programCategoriesType"]
  >({
    url: API_URLS.PROGRAM_CATEGORIES,
  });

  const {
    mutate: uploadPhoto,
    isLoading: isUploadingPhoto,
    error: uploadError,
  } = usePostApi({
    url: API_URLS.UPLOAD_FILE,
    withFormData: true,
    onSuccess: (data: UploadFileResponseType) => {
      methods.setValue("featureImage", data.filePath);
    },
  });

  const {
    refetch,
    isRefetching,
    isLoading: isLoadingItem,
  } = useGetItemApi<BadgeDetailsDtoType>({
    url: API_URLS.BADGES,
    id: id!,
    onSuccess: (data: BadgeDetailsDtoType) => {
      if (id) {
        methods.setValue(`featureImage`, data.featureImage);
        methods.setValue(`programCategoryId`, data.programCategoryId);
        setFeatureImage(data.featureImage);
        setDataToSend({
          programCategoryId: data.programCategoryId,
          featureImage: data.featureImage,
        });
      }
    },
  });

  const {
    mutate: addBadgeCtg,
    isLoading,
    error: addError,
  } = usePostApi<IAddCategoryBadge>({
    url: API_URLS.BADGES,
    onSuccess: () => {
      onClose();
      confirm.onFalse();
      methods.reset();
      setFeatureImage("");
    },
  });

  const {
    mutate: editBadgeCtg,
    isLoading: isEditLoading,
    error: editError,
  } = usePatchApi<IAddCategoryBadge>({
    url: API_URLS.BADGES,
    id: id!,
    onSuccess: () => {
      confirm.onFalse();
      onClose();
    },
  });

  const mutate = isEditing ? editBadgeCtg : addBadgeCtg;
  // #endregion Services

  // #region Handlers
  const handleDropSingleFile = useCallback((acceptedFiles: File[]) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setFeatureImage(
        Object.assign(newFile, {
          preview: URL.createObjectURL(newFile),
        })
      );
    }
  }, []);

  const onSubmit = useCallback(
    async (data: IAddCategoryBadge) => {
      if (!isEditing) data.badgeType = "PROGRAM_CATEGORY";
      confirm.onTrue();
      setDataToSend(data);
    },
    [confirm]
  );
  // #endregion Handlers

  // #region useEffect
  useEffect(() => {
    if (featureImage && typeof featureImage !== "string") {
      uploadPhoto(featureImage);
      methods.clearErrors("featureImage");
    }
  }, [featureImage]);

  useEffect(() => {
    open && refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // #endregion useEffect

  return (
    <>
      <Dialog
        open={open && !confirm.value}
        onClose={onClose}
        PaperProps={{ sx: { borderRadius: "4px" } }}
        fullWidth
      >
        <DialogContent>
          <Box p={{ md: 8, xs: 3 }}>
            <AppLoadingAndErrorWrapper
              isLoading={
                isRefetching ||
                isLoadingItem ||
                isLoading ||
                isUploadingPhoto ||
                isEditLoading
              }
              errorMessage={addError || editError || uploadError}
            >
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography fontSize={28} fontWeight={500} align='center'>
                      Add new category Badge
                    </Typography>
                  </Grid>
                  {!isEditing && (
                    <Grid item xs={12}>
                      <Block label='Category related to'>
                        <RHFSelect name='programCategoryId'>
                          {programCategories?.items.map(program => (
                            <MenuItem key={program.id} value={program.id}>
                              {program.name}
                            </MenuItem>
                          ))}
                        </RHFSelect>
                      </Block>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Block label='Upload badge'>
                      <Upload
                        file={featureImage}
                        error={!!errors.featureImage}
                        onDrop={handleDropSingleFile}
                        onDelete={() => {
                          setFeatureImage(null);
                          methods.setValue("featureImage", "");
                        }}
                      />
                    </Block>
                  </Grid>
                  <Grid item xs={6}>
                    <AppButton
                      label={"Cancel"}
                      color='secondary'
                      onClick={onClose}
                      fullWidth={true}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AppLoadingButton
                      label={"Upload & Save"}
                      isLoading={isLoading}
                    />
                  </Grid>
                </Grid>
              </FormProvider>
            </AppLoadingAndErrorWrapper>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Confirm */}
      <ConfirmDialog
        open={confirm.value}
        onClose={(
          event: React.SyntheticEvent<Element, Event>,
          reason: string
        ) => {
          if (reason && reason === "backdropClick") {
            confirm.onFalse();
            onClose();
          }
        }}
        content={`Are you sure you want to ${
          isEditing ? "edit" : "add"
        } this badges category?`}
        icon={<BorderColorOutlinedIcon fontSize='large' sx={{ mx: "auto" }} />}
        action={
          <>
            <AppButton
              fullWidth
              label='No'
              color='secondary'
              onClick={() => {
                confirm.onFalse();
                onClose();
              }}
            />
            <AppLoadingButton
              label='Yes'
              isLoading={isLoading || isEditLoading}
              onClick={() => {
                mutate(dataToSend!);
              }}
            />
          </>
        }
      />
    </>
  );
};

export default AddCategoryBadge;
